/**
 * Entitlement controls for the Semprion HQ console (`/admin`).
 *
 * Matched to BackOfficePro's admin screens — AJ, 2026-10-16: "I want OL to look
 * just like BOP for all customers ... I spent lots of effort getting BOP UI
 * right", then "match the pane content too". So the structure below is BOP's,
 * not an approximation of it:
 *
 *   BOP Company Management          OL Customer Management
 *   ──────────────────────          ──────────────────────
 *   company row (logo, email,       customer row (logo, slug, N organizations)
 *     N stores) expands to…           expands to…
 *   Company Logo | Stores (N)       Logo | Organizations (N)
 *   per store: name, store #,       per org: name, slug, tax id, teams, sports,
 *     tax, address, Subscription      address, Subscription Plan box,
 *     Tier box, Feature Access        Feature Access panel
 *
 * The analogue holds because OL nests league → organizations where BOP nests
 * company → stores, and every field BOP shows has a real OL counterpart
 * (`logo_url`, `contact.address1/city/state`, `tax_id`, `sports`, teams). None
 * of it is invented to fill a slot.
 *
 * Where OL genuinely differs: it can bill at EITHER level, so a league's own
 * plan and overrides sit in the left column (they govern every organization
 * below) while each organization keeps its own panel on the right.
 *
 * Rows render from `/api/entitlements/registry`, never a hardcoded list, so a
 * feature added to `backend/utils/features.py` appears here with no frontend
 * edit. BOP writes each row as literal JSX plus three more hardcoded copies in
 * one hook (its D2/D4), and hardcoding would break OL's own no-hardcoding rule.
 *
 * A slider writes a per-entity OVERRIDE into its own field; it is not a
 * materialised flag, which is why a later plan change cannot erase it.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import ThreePositionSlider from '../ThreePositionSlider';
import { Input } from '../ui/input';
import { roleName } from '../../config/roleNames';
import {
  Loader2, Check, X, GripVertical, Settings, Calendar, ClipboardList,
  DollarSign, Globe, Image as ImageIcon, Users, MessageSquare, BarChart3,
  Trophy, HandHeart, Smartphone, Plug, Layers, MapPin, Building2, LogIn,
  Pencil, ExternalLink, Save,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURE_ICONS = {
  scheduling_engine: Calendar,
  registration_engine: ClipboardList,
  finance_engine: DollarSign,
  website_engine: Globe,
  media_engine: ImageIcon,
  members_engine: Users,
  comms_engine: MessageSquare,
  stats_engine: BarChart3,
  gameday_engine: Trophy,
  tournaments: Trophy,
  volunteer: HandHeart,
  mobile_app: Smartphone,
  api_access: Plug,
  multi_org: Layers,
};

/** The feature registry, fetched once and shared by every pane. */
export function useFeatureRegistry() {
  const [features, setFeatures] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/api/entitlements/registry`)
      .then((r) => setFeatures(
        Object.entries(r.data?.features || {}).map(([key, meta]) => ({ key, ...meta }))))
      .catch(() => setFeatures([]));
  }, []);
  return features;
}

/** A plan's grants as {key: on|restrict}; tolerates the plain-list form. */
export function tierGrants(tier) {
  const fk = tier?.feature_keys;
  if (fk && typeof fk === 'object' && !Array.isArray(fk)) return fk;
  if (Array.isArray(fk)) return fk.reduce((a, k) => ({ ...a, [k]: 'on' }), {});
  return {};
}

/* ══ Billing pane ═══════════════════════════════════════════════════════ */

export function TierFeatureGrants({ tier, features, onSaved }) {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  const [over, setOver] = useState(false);
  const grants = tierGrants(tier);
  const byKey = Object.fromEntries(features.map((f) => [f.key, f]));
  // BOP lists ONLY the features a plan includes. Listing all of them dimmed
  // made the card unreadable at four-across, which is the whole point of the
  // layout — you compare plans by scanning what each one actually has.
  const included = Object.keys(grants)
    .filter((k) => byKey[k])
    .sort((a, b) => features.findIndex((f) => f.key === a) - features.findIndex((f) => f.key === b));

  const write = async (next, key) => {
    setBusy(key); setErr('');
    try {
      await axios.put(`${API_URL}/api/billing/tiers/${tier.tier}`, { feature_keys: next });
      await onSaved?.();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not update this plan.');
    }
    setBusy(null);
  };

  const setLevel = (key, level) => {
    const next = { ...grants };
    if (level === 'off') delete next[key]; else next[key] = level;
    write(next, key);
  };

  const onDrop = (e) => {
    e.preventDefault(); setOver(false);
    const key = e.dataTransfer.getData('text/ol-feature');
    if (key && !grants[key]) write({ ...grants, [key]: 'on' }, key);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted-foreground">
          {included.length} feature{included.length === 1 ? '' : 's'} included
        </span>
        {busy && <Loader2 className="h-3 w-3 animate-spin text-blue-600" />}
      </div>
      {err && <p className="text-[11px] text-red-600 mb-1">{err}</p>}
      <div
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        data-testid={`tier-grants-${tier.tier}`}
        className={`space-y-2 min-h-[200px] p-2 rounded-md border-2 border-dashed transition-colors ${
          over ? 'border-blue-400 bg-blue-50' : 'border-zinc-200 bg-zinc-50'}`}
      >
        {included.length === 0 && (
          <p className="text-xs text-muted-foreground">Drop features here</p>
        )}
        {included.map((key) => {
          const f = byKey[key];
          const Icon = FEATURE_ICONS[key] || Settings;
          return (
            <div key={key}
              className="group flex items-center gap-1.5 px-1.5 py-1.5 rounded-md bg-white border border-zinc-200"
              data-testid={`granted-${tier.tier}-${key}`}>
              <button type="button" onClick={() => setLevel(key, 'off')}
                title="Remove from this plan"
                data-testid={`remove-${tier.tier}-${key}`}
                className="w-0 group-hover:w-3 overflow-hidden transition-all text-zinc-300 hover:text-red-600 shrink-0">
                <X className="w-3 h-3" />
              </button>
              <Icon className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
              <span className="text-xs flex-1 min-w-0 truncate" title={f.name + ' — ' + f.description}>
                {f.name}
                {f.status === 'planned' && (
                  <span className="ml-1 text-[9px] uppercase text-amber-600" title="Not built yet">
                    planned
                  </span>
                )}
              </span>
              <select value={grants[key]} onChange={(e) => setLevel(key, e.target.value)}
                data-testid={`access-${tier.tier}-${key}`}
                className="h-6 w-[72px] text-[11px] shrink-0 rounded border border-input bg-white px-0.5">
                <option value="on">On</option>
                <option value="restrict">Restricted</option>
                <option value="off">Remove</option>
              </select>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** BOP's draggable "Available Features" rail. */
export function FeatureRail({ features, onRenamed }) {
  // BOP's "Edit Feature Display": Semprion renames what a customer SEES without
  // a deploy. The key itself is never editable — it is what actually gates, and
  // what tier grants, overrides and the page catalog all reference.
  const [renaming, setRenaming] = useState(null);
  const [draft, setDraft] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  const openRename = (f) => {
    setRenaming(f);
    setDraft({ name: f.name || '', description: f.description || '' });
  };
  const saveRename = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/entitlements/features/${renaming.key}/display`, draft);
      setRenaming(null);
      await onRenamed?.();
    } catch { /* leave the dialog open so the operator can retry */ }
    setSaving(false);
  };

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <p className="text-base font-semibold">Available Features</p>
      <p className="text-xs text-muted-foreground mb-2">Drag features to plans</p>

      {renaming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
             onClick={() => setRenaming(null)}>
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4 p-5 space-y-3"
               onClick={(e) => e.stopPropagation()} data-testid="feature-display-dialog">
            <div>
              <h3 className="text-base font-semibold">Edit Feature Display</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Changes what customers see. The feature key
                <code className="mx-1 text-foreground">{renaming.key}</code>
                never changes — it is what gates the pages.
              </p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Display Name</label>
              <Input value={draft.name} autoFocus data-testid="feature-display-name"
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                className="rounded-sm mt-1" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Description</label>
              <Input value={draft.description} data-testid="feature-display-description"
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                className="rounded-sm mt-1" />
            </div>
            <div className="flex justify-between gap-2 pt-1">
              <Button variant="ghost" size="sm" className="text-muted-foreground"
                onClick={() => setDraft({ name: '', description: '' })}>
                Reset to default
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setRenaming(null)}>Cancel</Button>
                <Button size="sm" onClick={saveRename} disabled={saving}
                  data-testid="feature-display-save"
                  className="bg-blue-600 hover:bg-blue-700 text-white">Save</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2 max-h-[520px] overflow-y-auto">
        {features.map((f) => {
          const Icon = FEATURE_ICONS[f.key] || Settings;
          return (
            <div key={f.key} draggable
              onDragStart={(e) => e.dataTransfer.setData('text/ol-feature', f.key)}
              data-testid={`draggable-feature-${f.key}`}
              className="group flex items-center gap-2 p-2 bg-zinc-50 rounded-md cursor-grab hover:bg-zinc-100 transition-colors border border-zinc-200">
              <GripVertical className="w-4 h-4 text-zinc-400" />
              <Icon className="w-4 h-4 text-zinc-600" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {f.name}
                  {/* A planned feature can be priced and put on a plan, but
                      nothing is wired behind it. Saying so here is what stops
                      it being sold as though it worked. */}
                  {f.status === 'planned' && (
                    <span className="ml-1.5 text-[9px] uppercase tracking-wide px-1 py-0.5 rounded bg-amber-100 text-amber-700 align-middle">
                      planned
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground truncate">{f.description}</p>
              </div>
              <button type="button" title="Edit display name"
                data-testid={`rename-feature-${f.key}`}
                onClick={(e) => { e.stopPropagation(); openRename(f); }}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1
                           text-muted-foreground hover:text-blue-600 shrink-0">
                <Pencil className="w-3 h-3" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ══ Customers pane ═════════════════════════════════════════════════════ */

/** BOP's per-store "Feature Access" panel, for one OL entity. */
function FeatureAccessPanel({ scopeType, scopeId, features, resolved, overrides, onSave, saving }) {
  return (
    <div className="bg-zinc-50 rounded-sm p-3 w-full md:w-auto md:min-w-[340px]">
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold text-sm">Feature Access</div>
        <span className="text-[10px] text-muted-foreground">Admin can override</span>
      </div>
      <div className="grid grid-cols-2 gap-2 gap-x-3">
        {features.map((f) => {
          const ov = overrides[f.key];
          const eff = resolved?.features?.[f.key];
          // With no override the slider shows the RESOLVED value, so the panel
          // always reads as what this entity actually has today.
          const shown = ov || eff || 'off';
          return (
            <div key={f.key} className="flex items-center justify-between gap-1"
              data-testid={`ent-row-${f.key}`}>
              <span className="text-muted-foreground text-xs truncate" title={f.description}>
                {f.name}{ov && <span className="text-emerald-600" title="overridden"> *</span>}
              </span>
              <div className="flex items-center gap-1 shrink-0">
                {saving === f.key && <Loader2 className="h-3 w-3 animate-spin text-blue-600" />}
                <ThreePositionSlider
                  value={shown}
                  onChange={(v) => onSave({ ...overrides, [f.key]: v }, f.key)}
                  size="sm" showLabels={false}
                  data-testid={`slider-${scopeType}-${scopeId}-${f.key}`}
                />
                {/* No "clear override" X (AJ 2026-10-18): HQ admins read it as "delete this
                    feature". The slider alone sets the value; * marks an override. */}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3 mt-3 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><i className="inline-block w-2 h-2 rounded-full bg-zinc-400" /> Off</span>
        <span className="flex items-center gap-1"><i className="inline-block w-2 h-2 rounded-full bg-amber-400" /> Restricted</span>
        <span className="flex items-center gap-1"><i className="inline-block w-2 h-2 rounded-full bg-green-500" /> On</span>
        <span className="ml-auto"><span className="text-emerald-600">*</span> overridden</span>
      </div>
    </div>
  );
}

/** The plan selector — BOP's blue "Subscription Tier" block. */
function PlanBox({ label, status, tiers, currentTier, onPick, note }) {
  const tone = status === 'suspended'
    ? 'bg-red-50 border-red-300'
    : (status === 'past_due' || status === 'overdue')
      ? 'bg-amber-50 border-amber-300'
      : 'bg-blue-50 border-blue-200';
  const text = status === 'suspended'
    ? 'text-red-700'
    : (status === 'past_due' || status === 'overdue') ? 'text-amber-700' : 'text-blue-700';
  return (
    <div className={`mt-3 p-2 rounded-sm border ${tone}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs font-medium ${text}`}>{label}:</span>
        {status === 'trial' && <Badge className="bg-blue-500 text-white text-[10px] px-1.5 py-0">TRIAL</Badge>}
        {status === 'suspended' && <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0">SUSPENDED</Badge>}
        <select value={currentTier || ''} onChange={(e) => onPick(e.target.value || null)}
          data-testid="plan-select"
          className="h-7 text-xs rounded border border-input bg-white px-1">
          <option value="">No plan</option>
          {tiers.map((t) => <option key={t.tier} value={t.tier}>{t.label || t.tier}</option>)}
        </select>
      </div>
      <p className="text-[10px] text-muted-foreground mt-1">
        {note || 'Controls feature access. Removing the plan returns this entity to unmanaged — every feature on — never to "nothing enabled".'}
      </p>
    </div>
  );
}

/** One organization: identity, plan, and its Feature Access panel. */
function OrganizationRow({ org, features, tiers, leagueGoverns, onChanged, onImpersonate }) {
  const [detail, setDetail] = useState(null);
  const [resolved, setResolved] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [saving, setSaving] = useState(null);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    setErr('');
    try {
      const d = await axios.get(`${API_URL}/api/admin/customers/${org.id}`);
      setDetail(d.data);
      setOverrides(d.data?.customer?.feature_overrides || {});
      const r = await axios.get(`${API_URL}/api/entitlements/org/${org.id}`);
      setResolved(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not load this organization.');
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const save = async (next, key) => {
    setSaving(key); setErr('');
    const prev = overrides;
    setOverrides(next);
    try {
      await axios.put(`${API_URL}/api/entitlements/org/${org.id}/overrides`, { feature_overrides: next });
      await load();
    } catch (e) {
      setOverrides(prev);
      setErr(e.response?.data?.detail || 'Could not save that change.');
    }
    setSaving(null);
  };

  const setPlan = async (tier) => {
    setErr('');
    try {
      await axios.put(`${API_URL}/api/entitlements/org/${org.id}/tier`, { tier });
      await load();
      await onChanged?.();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not set the plan.');
    }
  };

  const c = detail?.customer || {};
  const addr = c.contact || {};

  // One impersonation target per role that actually exists in this org, newest
  // first so an active account wins over a dormant one.
  const roleUsers = useMemo(() => {
    const seen = new Map();
    (detail?.users || []).forEach((u) => {
      (u.roles || []).forEach((r) => {
        if (r.org_id && r.org_id !== org.id) return;
        if (!r.role_type || seen.has(r.role_type)) return;
        seen.set(r.role_type, { role_type: r.role_type, user: u });
      });
    });
    return [...seen.values()];
  }, [detail, org.id]);

  const saveOrg = async () => {
    setErr('');
    const body = {};
    if (form.name) body.name = form.name;
    if (['address1', 'city', 'state', 'email'].some(k => form[k] !== undefined)) {
      body.contact = {
        ...addr,
        address1: form.address1 ?? addr.address1 ?? '',
        city: form.city ?? addr.city ?? '',
        state: form.state ?? addr.state ?? '',
        email: form.email ?? addr.email ?? null,
      };
    }
    if (Object.keys(body).length === 0) { setEditing(false); return; }
    try {
      await axios.put(`${API_URL}/api/organizations/${org.id}`, body);
      setEditing(false); setForm({});
      await load();
      await onChanged?.();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not save this organization.');
    }
  };
  const addressLine = [
    addr.address1, addr.address2,
    [addr.city, addr.state, addr.postal_code].filter(Boolean).join(' '),
  ].filter(Boolean).join('\n');

  return (
    <div className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-4">
        {/* Left — identity + plan */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-sm">{org.name}</span>
            <Button variant="ghost" size="sm"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-blue-600"
              onClick={() => setEditing(v => !v)} data-testid={`edit-org-${org.id}`}
              title="Edit this organization">
              <Pencil className="w-3 h-3" />
            </Button>
          </div>
          <div className="text-xs text-muted-foreground">
            {/* A slug if there is one; never the raw UUID, which tells an
                operator nothing and is where BOP shows a store number. */}
            {[c.slug || org.slug,
              c.tax_id ? `Tax ID: ${c.tax_id}` : null,
              c.founding_year ? `est. ${c.founding_year}` : null,
            ].filter(Boolean).join(' • ') || 'No slug set'}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {(detail?.teams?.length ?? 0)} teams
            {/* Sports come from the list payload, which the backend derives from
                the CONFIGURED sports collection. `organizations.sports` is stale
                legacy data — it still claims Soccer for a club that plays
                baseball — so it is deliberately not used here. */}
            {Array.isArray(org.sports) && org.sports.length > 0 ? ` • ${org.sports.join(', ')}` : ''}
          </div>
          {addressLine && (
            <div className="flex items-start gap-1 mt-2 text-xs text-muted-foreground">
              <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span className="whitespace-pre-line">{addressLine}</span>
            </div>
          )}

          {editing && (
            <div className="mt-2 p-2 rounded-sm border border-border bg-white space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Name</label>
                  <Input className="h-7 text-xs" defaultValue={c.name || org.name}
                    onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                    data-testid={`edit-name-${org.id}`} />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Address</label>
                  <Input className="h-7 text-xs" defaultValue={addr.address1 || ''}
                    onChange={(e) => setForm(f => ({ ...f, address1: e.target.value }))}
                    data-testid={`edit-address-${org.id}`} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">City</label>
                  <Input className="h-7 text-xs" defaultValue={addr.city || ''}
                    onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">State</label>
                  <Input className="h-7 text-xs" defaultValue={addr.state || ''}
                    onChange={(e) => setForm(f => ({ ...f, state: e.target.value }))} />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Contact email</label>
                  <Input className="h-7 text-xs" defaultValue={addr.email || ''}
                    onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="h-7 bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={saveOrg} data-testid={`save-org-${org.id}`}>
                  <Save className="w-3 h-3 mr-1" /> Save
                </Button>
                <Button size="sm" variant="outline" className="h-7"
                  onClick={() => { setEditing(false); setForm({}); }}>Cancel</Button>
              </div>
              <p className="text-[10px] text-muted-foreground">
                There is no delete here on purpose: OL has no organization-delete endpoint, and
                removing a club with its teams, players and history is not something to add behind
                a small icon.
              </p>
            </div>
          )}

          {/* Impersonation per ROLE, the way BOP offers Corporate / Superuser /
              Facilities / Manager under a store. The roles come from this
              organization's own users, so only roles that actually exist show. */}
          {roleUsers.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                View as
              </p>
              <div className="flex flex-wrap gap-1.5">
                {roleUsers.map(({ role_type, user }) => (
                  <Button key={role_type} variant="outline" size="sm"
                    className="h-7 text-[11px] rounded-sm"
                    onClick={() => onImpersonate?.({ ...org, impersonate_user_id: user.id })}
                    title={`Open the app as ${user.email}`}
                    data-testid={`view-as-${org.id}-${role_type}`}>
                    {roleName(role_type)}
                    <ExternalLink className="w-3 h-3 ml-1 opacity-60" />
                  </Button>
                ))}
              </div>
            </div>
          )}

          <PlanBox
            label="Subscription Plan"
            status={c.subscription_status || org.subscription_status}
            tiers={tiers}
            currentTier={c.tier || org.tier}
            onPick={setPlan}
            note={leagueGoverns
              ? "The league's plan governs this organization right now, so a plan set here applies only if the league has none. Overrides on the right still win."
              : undefined}
          />
          {err && <p className="text-[11px] text-red-600 mt-1">{err}</p>}
          {resolved && (
            <p className="text-[10px] text-muted-foreground mt-1">
              Resolved: {resolved.unmanaged
                ? 'unmanaged — every feature on'
                : `${resolved.tier_label || resolved.tier} · governed by ${resolved.governed_by}`}
            </p>
          )}
        </div>

        {/* Right — Feature Access */}
        <FeatureAccessPanel
          scopeType="org" scopeId={org.id} features={features}
          resolved={resolved} overrides={overrides} onSave={save} saving={saving}
        />
      </div>
    </div>
  );
}

/** The expanded customer card — BOP's "Company Logo | Stores (N)" layout. */
export function CustomerDetail({ customer, organizations = [], features, tiers, onChanged, onImpersonate }) {
  const isLeague = customer?.type === 'league';
  const [detail, setDetail] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [resolved, setResolved] = useState(null);
  const [saving, setSaving] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    if (!customer?.id) return;
    setErr('');
    try {
      const d = await axios.get(`${API_URL}/api/admin/customers/${customer.id}`);
      setDetail(d.data);
      setOverrides(d.data?.customer?.feature_overrides || {});
      if (isLeague) {
        const first = (d.data?.organizations || [])[0];
        if (first) {
          const r = await axios.get(`${API_URL}/api/entitlements/org/${first.id}`);
          setResolved(r.data);
        } else {
          setResolved({ features: {}, sources: {}, noOrgs: true });
        }
      }
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not load this customer.');
    }
  }, [customer?.id, isLeague]);

  useEffect(() => { load(); }, [load]);

  const saveLeagueOverrides = async (next, key) => {
    setSaving(key); setErr('');
    const prev = overrides;
    setOverrides(next);
    try {
      await axios.put(`${API_URL}/api/entitlements/league/${customer.id}/overrides`,
        { feature_overrides: next });
      await load();
      await onChanged?.();
    } catch (e) {
      setOverrides(prev);
      setErr(e.response?.data?.detail || 'Could not save that change.');
    }
    setSaving(null);
  };

  const setLeaguePlan = async (tier) => {
    try {
      await axios.put(`${API_URL}/api/entitlements/league/${customer.id}/tier`, { tier });
      await load();
      await onChanged?.();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not set the plan.');
    }
  };

  const c = detail?.customer || {};
  const logo = c.logo_url
    ? (c.logo_url.startsWith('http') ? c.logo_url : `${API_URL}${c.logo_url}`)
    : c.wp_logo_url;

  // For a league the list is its organizations; a club sold on its own is a
  // customer whose only entity is itself — BOP's one-store company.
  const orgs = isLeague ? (detail?.organizations || organizations) : [customer];
  const leagueGoverns = isLeague && !!c.tier;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-4" data-testid="customer-entitlements">
      {/* Left — logo, and for a league its own plan + overrides */}
      <div className="space-y-3">
        <div>
          <div className="flex items-center gap-1 text-sm font-semibold mb-1">
            <ImageIcon className="w-4 h-4 text-muted-foreground" />
            {isLeague ? 'League Logo' : 'Organization Logo'}
          </div>
          <div className="bg-white border border-border rounded-sm p-3 flex flex-col items-center justify-center min-h-[120px]">
            {logo ? (
              <img src={logo} alt={`${customer.name} logo`} className="max-h-24 object-contain" />
            ) : (
              <div className="text-xs text-muted-foreground text-center">
                <Building2 className="w-6 h-6 mx-auto mb-1 opacity-40" />
                No logo on file
              </div>
            )}
            <p className="text-[10px] text-muted-foreground mt-2">
              {isLeague ? 'Shown on league pages' : 'Applies to the public site'}
            </p>
          </div>
        </div>

        {isLeague && (
          <div>
            <div className="text-sm font-semibold mb-1">League Plan</div>
            <div className="bg-white border border-border rounded-sm p-3">
              <p className="text-[11px] text-muted-foreground">
                {orgs.length} organization(s). A plan here governs every one of them; an
                organization&rsquo;s own plan applies only when the league has none.
              </p>
              <PlanBox
                label="League Plan"
                status={c.subscription_status || customer.subscription_status}
                tiers={tiers}
                currentTier={c.tier || customer.tier}
                onPick={setLeaguePlan}
              />
              {err && <p className="text-[11px] text-red-600 mt-1">{err}</p>}
              {detail && (
                <div className="mt-3">
                  <FeatureAccessPanel
                    scopeType="league" scopeId={customer.id} features={features}
                    resolved={resolved} overrides={overrides}
                    onSave={saveLeagueOverrides} saving={saving}
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    League overrides apply to every organization below. A per-organization
                    override still beats them.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Right — the organizations, BOP's Stores list */}
      <div>
        <div className="flex items-center gap-1 text-sm font-semibold mb-1">
          <Building2 className="w-4 h-4 text-muted-foreground" />
          {isLeague ? `Organizations (${orgs.length})` : 'Organization'}
        </div>
        <div className="bg-white border border-border rounded-sm divide-y divide-border max-h-[600px] overflow-y-auto">
          {orgs.length > 0 ? orgs.map((o) => (
            <OrganizationRow key={o.id} org={o} features={features} tiers={tiers}
              leagueGoverns={leagueGoverns} onChanged={onChanged} onImpersonate={onImpersonate} />
          )) : (
            <div className="p-6 text-center text-xs text-muted-foreground">
              This league has no organizations yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Kept so the older import name does not break the build.
export const CustomerEntitlements = CustomerDetail;
